import { useEffect, useState } from "react";
import { useNotificationContext } from "contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { RouteName } from "constants/route";
import { ICreateDriver, IDriverInput } from "models/driver";
import { createDriverApi } from "api/driver";
import { ILoadingData } from "models";
import { activeFleetModelsApi } from "api/fleetModel";
import { IFleetModel } from "models/fleetModel";
import { IFleetInput } from "models/fleet";
import { IDriverWalletInput } from "models/driverWallet";
import {
  uploadBoloApi,
  uploadBusinessLicenseApi,
  uploadDriverApi,
  uploadDriverIdApi,
  uploadDriverLicenseApi,
  uploadEmploymentAgreementApi,
  uploadFleetApi,
  uploadInsuranceSubscriptionApi,
  uploadLicenseApi,
  uploadMaheberSupportApi,
  uploadOwnerIdApi,
} from "api/upload";
import { RcFile } from "antd/es/upload";

export default function useCreateDriver() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotificationContext();
  const [stepper, setStepper] = useState<number>(1);
  const [data, setData] = useState<ICreateDriver>();
  const [loading, setLoading] = useState<boolean>(false);

  const [fleetModels, setFleetModels] = useState<ILoadingData<IFleetModel>>({
    data: [],
    loading: true,
  });

  useEffect(() => {
    activeFleetModelsApi().then(({ data, error }) => {
      if (data) setFleetModels({ data: data.data, loading: false });
      else showError(error!.message);
    });
    // eslint-disable-next-line
  }, []);

  const onDriverInfoSubmit = (input: IDriverInput) => {
    setData((prev) =>
      prev ? { ...prev, driver: input } : ({ driver: input } as any)
    );
    setStepper(2);
  };
  const onFleetInfoSubmit = (input: IFleetInput) => {
    setData((prev) => ({ ...prev!, fleet: input }));
    setStepper(3);
  };
  const onWalletSubmit = async (input: IDriverWalletInput) => {
    setLoading(true);
    try {
      if (
        data!.fleet.image &&
        Array.isArray(data!.fleet.image) &&
        data!.fleet.image.length > 0
      )
        data!.fleet.image = (
          data!.fleet.image[0] as unknown as { url: string }
        ).url;
      else data!.fleet.image = null;
      if (
        data!.fleet.license_image &&
        Array.isArray(data!.fleet.license_image) &&
        data!.fleet.license_image.length > 0
      )
        data!.fleet.license_image = (
          data!.fleet.license_image[0] as unknown as { url: string }
        ).url;
      else data!.fleet.license_image = null;
      if (Array.isArray(data!.driver.image) && data!.driver.image.length > 0)
        data!.driver.image = (
          data!.driver.image[0] as unknown as { url: string }
        ).url;
      else data!.driver.image = null;
      if (
        Array.isArray(data!.driver.driving_license_image) &&
        data!.driver.driving_license_image.length > 0
      )
        data!.driver.driving_license_image = (
          data!.driver.driving_license_image[0] as unknown as { url: string }
        ).url;
      else data!.driver.driving_license_image = null;
      if (
        Array.isArray(data!.driver.insurance_subscription_image) &&
        data!.driver.insurance_subscription_image.length > 0
      )
        data!.driver.insurance_subscription_image = (
          data!.driver.insurance_subscription_image[0] as unknown as {
            url: string;
          }
        ).url;
      else data!.driver.insurance_subscription_image = null;
      if (
        Array.isArray(data!.driver.employment_agreement_image) &&
        data!.driver.employment_agreement_image.length > 0
      )
        data!.driver.employment_agreement_image = (
          data!.driver.employment_agreement_image[0] as unknown as {
            url: string;
          }
        ).url;
      else data!.driver.employment_agreement_image = null;
      if (
        Array.isArray(data!.driver.owner_id_image) &&
        data!.driver.owner_id_image.length > 0
      )
        data!.driver.owner_id_image = (
          data!.driver.owner_id_image[0] as unknown as { url: string }
        ).url;
      else data!.driver.owner_id_image = null;
      if (
        Array.isArray(data!.driver.driver_id_image) &&
        data!.driver.driver_id_image.length > 0
      )
        data!.driver.driver_id_image = (
          data!.driver.driver_id_image[0] as unknown as { url: string }
        ).url;
      else data!.driver.driver_id_image = null;
      if (
        Array.isArray(data!.driver.maheber_support_image) &&
        data!.driver.maheber_support_image.length > 0
      )
        data!.driver.maheber_support_image = (
          data!.driver.maheber_support_image[0] as unknown as { url: string }
        ).url;
      else data!.driver.maheber_support_image = null;
      if (
        Array.isArray(data!.driver.bolo_image) &&
        data!.driver.bolo_image.length > 0
      )
        data!.driver.bolo_image = (
          data!.driver.bolo_image[0] as unknown as { url: string }
        ).url;
      else data!.driver.bolo_image = null;
      if (
        Array.isArray(data!.driver.business_license_images) &&
        data!.driver.business_license_images.length > 0
      )
        data!.driver.business_license_images = (
          data!.driver.business_license_images as unknown as { url: string }[]
        ).map((file) => file.url);
      else data!.driver.business_license_images = null;
      const { data: response, error } = await createDriverApi({
        ...data!,
        wallet: input,
      });
      if (response) {
        showSuccess("Driver created Successfully");
        navigate(RouteName.DRIVER, { replace: true });
      } else {
        showError(error!.message);
      }
    } catch (error) {
      showError("An error occurred while creating the driver");
      setStepper(1);
    }
    setLoading(false);
  };

  const handleStepper1 = () => setStepper(1);
  const handleStepper2 = () => setStepper(2);
  const handleStepper3 = () => setStepper(3);
  const handleBackStepper = () =>
    setStepper((prev) => (prev > 1 ? prev - 1 : prev));

  const handleUploadDriverImage = async (file: RcFile) => {
    const { data, error } = await uploadDriverApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadDriverLicenseImage = async (file: RcFile) => {
    const { data, error } = await uploadDriverLicenseApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadInsuranceSubscriptionImage = async (file: RcFile) => {
    const { data, error } = await uploadInsuranceSubscriptionApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadEmploymentAgreementImage = async (file: RcFile) => {
    const { data, error } = await uploadEmploymentAgreementApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadMaheberSupportImage = async (file: RcFile) => {
    const { data, error } = await uploadMaheberSupportApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadDriverIdImage = async (file: RcFile) => {
    const { data, error } = await uploadDriverIdApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadOwnerIdImage = async (file: RcFile) => {
    const { data, error } = await uploadOwnerIdApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadBoloImage = async (file: RcFile) => {
    const { data, error } = await uploadBoloApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadBusinessLicenseImage = async (file: RcFile) => {
    const { data, error } = await uploadBusinessLicenseApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };
  const handleUploadFleetImage = async (file: RcFile) => {
    const { data, error } = await uploadFleetApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };

  const handleUploadLicenseImage = async (file: RcFile) => {
    const { data, error } = await uploadLicenseApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };

  return {
    loading,
    fleetModels,
    stepper,
    handleBackStepper,
    onDriverInfoSubmit,
    onFleetInfoSubmit,
    onWalletSubmit,
    handleStepper1,
    handleStepper2,
    handleStepper3,
    handleUploadDriverImage,
    handleUploadDriverLicenseImage,
    handleUploadInsuranceSubscriptionImage,
    handleUploadEmploymentAgreementImage,
    handleUploadMaheberSupportImage,
    handleUploadDriverIdImage,
    handleUploadOwnerIdImage,
    handleUploadBoloImage,
    handleUploadBusinessLicenseImage,
    handleUploadFleetImage,
    handleUploadLicenseImage,
  };
}
